import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';

const CATEGORIES = ['Eletrônicos', 'Veículos', 'Imóveis', 'Moda', 'Casa e Decoração', 'Esportes', 'Livros', 'Serviços', 'Digital', 'Outros'];
const CONDITIONS = [{ value: 'new', label: 'Novo' }, { value: 'used', label: 'Usado' }];
const SORTS = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'price_asc', label: 'Menor preço' },
  { value: 'price_desc', label: 'Maior preço' },
  { value: 'popular', label: 'Mais populares' },
];

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const condition = searchParams.get('condition') || '';
  const sort = searchParams.get('sort') || 'recent';
  const page = parseInt(searchParams.get('page') || '1');
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const state = searchParams.get('state') || '';
  const city = searchParams.get('city') || '';

  useEffect(() => {
    api.get('/locations/states').then(r => setStates(r.data.states)).catch(() => {});
  }, []);

  useEffect(() => {
    if (state) {
      api.get(`/locations/cities/${state}`).then(r => setCities(r.data.cities)).catch(() => setCities([]));
    } else {
      setCities([]);
    }
  }, [state]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort };
      if (category) params.category = category;
      if (search) params.search = search;
      if (condition) params.condition = condition;
      if (minPrice) params.min_price = minPrice;
      if (maxPrice) params.max_price = maxPrice;
      if (state) params.state = state;
      if (city) params.city = city;
      const { data } = await api.get('/products', { params });
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [category, search, condition, sort, page, minPrice, maxPrice, state, city]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => setSearchParams({});

  const hasActiveFilters = category || condition || minPrice || maxPrice || state || city;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="products-page">
      {/* Promotional Banner */}
      <div className="mb-6 rounded-2xl overflow-hidden shadow-md" data-testid="products-promo-banner">
        <img
          src="/banner.png"
          alt="Venda de Garagem - Achou, Levou, Sorriu!"
          className="w-full h-auto"
        />
      </div>

      {/* Search header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>
            {search ? `Resultados para "${search}"` : category ? category : 'Todos os Produtos'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{total} produto{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sort} onValueChange={(v) => updateFilter('sort', v)}>
            <SelectTrigger className="w-[160px]" data-testid="sort-select">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => setShowFilters(!showFilters)} className="lg:hidden" data-testid="toggle-filters-btn">
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Filters sidebar */}
        <aside className={`${showFilters ? 'fixed inset-0 z-50 bg-background p-6 overflow-auto' : 'hidden'} lg:block lg:static lg:w-56 shrink-0 space-y-6`} data-testid="filters-sidebar">
          <div className="flex items-center justify-between lg:hidden">
            <h3 className="font-semibold">Filtros</h3>
            <button onClick={() => setShowFilters(false)}><X className="h-5 w-5" /></button>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Categoria</h4>
            <div className="space-y-1">
              {CATEGORIES.map(c => (
                <button
                  key={c}
                  onClick={() => updateFilter('category', category === c ? '' : c)}
                  className={`block w-full text-left text-sm px-3 py-1.5 rounded-lg transition-colors ${category === c ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                  data-testid={`filter-cat-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Condition */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Condição</h4>
            <div className="flex gap-2">
              {CONDITIONS.map(c => (
                <button
                  key={c.value}
                  onClick={() => updateFilter('condition', condition === c.value ? '' : c.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${condition === c.value ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary'}`}
                  data-testid={`filter-cond-${c.value}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Preço (R$)</h4>
            <div className="flex items-center gap-2">
              <Input type="number" placeholder="Mín" value={minPrice} onChange={e => updateFilter('min_price', e.target.value)} className="h-9 text-sm" data-testid="filter-min-price" />
              <span className="text-sm text-muted-foreground">-</span>
              <Input type="number" placeholder="Máx" value={maxPrice} onChange={e => updateFilter('max_price', e.target.value)} className="h-9 text-sm" data-testid="filter-max-price" />
            </div>
          </div>

          {/* Location */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> Localização</h4>
            <div className="space-y-2">
              <Select value={state} onValueChange={v => { updateFilter('state', v); updateFilter('city', ''); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-state-select">
                  <SelectValue placeholder="Todos os estados" />
                </SelectTrigger>
                <SelectContent>
                  {states.map(s => <SelectItem key={s.uf} value={s.uf}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {state && cities.length > 0 && (
                <Select value={city} onValueChange={v => updateFilter('city', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="filter-city-select">
                    <SelectValue placeholder="Todas as cidades" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {state && (
                <button onClick={() => { updateFilter('state', ''); updateFilter('city', ''); }} className="text-xs text-primary hover:underline" data-testid="clear-location-btn">
                  Limpar localização
                </button>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-sm" data-testid="clear-filters-btn">
              <X className="h-3 w-3 mr-1" /> Limpar filtros
            </Button>
          )}

          <div className="lg:hidden pt-4">
            <Button onClick={() => setShowFilters(false)} className="w-full">Aplicar filtros</Button>
          </div>
        </aside>

        {/* Products Grid */}
        <div className="flex-1">
          {/* Active filters badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {category && <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilter('category', '')}>{category} <X className="h-3 w-3 ml-1" /></Badge>}
              {condition && <Badge variant="secondary" className="cursor-pointer" onClick={() => updateFilter('condition', '')}>{condition === 'new' ? 'Novo' : 'Usado'} <X className="h-3 w-3 ml-1" /></Badge>}
              {state && <Badge variant="secondary" className="cursor-pointer flex items-center gap-1" onClick={() => { updateFilter('state', ''); updateFilter('city', ''); }}><MapPin className="h-3 w-3" /> {states.find(s => s.uf === state)?.name || state}{city ? ` - ${city}` : ''} <X className="h-3 w-3 ml-1" /></Badge>}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-lg border border-border overflow-hidden animate-pulse">
                  <div className="aspect-square bg-muted" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-5 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Nenhum produto encontrado</h3>
              <p className="text-sm text-muted-foreground mb-4">Tente ajustar seus filtros ou busca.</p>
              <Button onClick={clearFilters} variant="outline" data-testid="empty-clear-filters">Limpar filtros</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="products-grid">
                {products.map(p => <ProductCard key={p._id} product={p} />)}
              </div>
              {/* Pagination */}
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: Math.min(pages, 5) }).map((_, i) => (
                    <Button
                      key={i}
                      variant={page === i + 1 ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateFilter('page', String(i + 1))}
                      data-testid={`page-${i + 1}`}
                    >
                      {i + 1}
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
